import { isArray } from 'lodash-es';
import { useMemo, useRef } from 'react';

import { BackorderLogic } from '~/api/BacorderLogic';
import { company } from '~/company/Company';
import { MAX_COUNT_TO_SHOW, OFFERS_LIMIT } from '~/constants/variables';
import { useMounted } from '~/hooks/useMounted';
import { orderStore, settingStore, userStore } from '~/stores';
import type { Offer } from '~/stores/CategoriesStore';

export type StockSource = Pick<
  Offer,
  | 'sellableWarehouses'
  | 'stocks'
  | 'isBackorderAvailable'
  | 'multibuyPriceLevels'
  | 'sellable'
>;

/**
 * Catalogue client routes are requested without a warehouse code, so
 * `sellableWarehouses` carries ALL active warehouses instead of just the
 * current one and its parent. There the legacy "parent" fallback (the only
 * other key of the map) is ambiguous, so the fallback quantity is taken from
 * the priority-ordered `stocks` array: the highest-priority warehouse other
 * than the current one. Returns null for legacy responses without `stocks`
 * (endpoints still requested with a warehouse code), where the old
 * two-key reduce stays correct.
 */
const getPriorityParentStock = (
  offer: StockSource | null | undefined,
  whCode: string,
): number | null => {
  const stocks = offer?.stocks;

  if (!stocks?.length) {
    return null;
  }

  const parent = stocks.find((item) => item.warehouseCode !== whCode);

  return parent ? Math.max(0, Number(parent.stock) || 0) : 0;
};

/**
 * LMS companies sell through a virtual storefront warehouse: ETA points at it
 * (VWH-*), while the stock sits on fulfilment warehouses and the V3
 * calculation routes across all of them. The purchasable quantity is then the
 * best single warehouse's stock, not the ETA warehouse's (which holds none).
 */
const getLmsSellable = (offer: StockSource | null | undefined): number =>
  Math.max(
    0,
    ...Object.values(offer?.sellableWarehouses ?? {}).map(
      (value) => Number(value) || 0,
    ),
  );

export const useProductLimit = ({ offer }: { offer?: StockSource | null }) => {
  const mounted = useMounted();
  const sellableWhPrev = useRef({ current: 0, parent: 0 });
  // The warehouse (and express availability) come from persisted stores
  // (deliveryAddress / etaCalculation), which are empty on the server. Reading
  // them during the first client render would pick a different warehouse than
  // SSR and flip stock-derived UI (out-of-stock overlay, limit), breaking
  // hydration. Gate behind mount so the first client render matches SSR (the
  // default warehouse); the real warehouse applies post-mount.
  const whCode =
    (mounted &&
      userStore.deliveryAddress &&
      orderStore.etaCalculation?.warehouse?.code) ||
    company.config.warehouse.default;
  const sellableInWh =
    (offer &&
      offer?.sellableWarehouses &&
      Object.keys(offer.sellableWarehouses)) ??
    [];

  const sellableItemsByWh = useMemo(() => {
    if (sellableInWh.includes(whCode)) {
      const reduced = isArray(sellableInWh)
        ? sellableInWh.reduce(
            (acc, key) => ({
              ...acc,
              [key === whCode ? 'current' : 'parent']:
                (Number(offer?.sellableWarehouses[key]) || 0) <= 0
                  ? 0
                  : offer?.sellableWarehouses[key],
            }),
            { current: 0, parent: 0 },
          )
        : { current: sellableInWh, parent: 0 };
      const priorityParent = getPriorityParentStock(offer, whCode);
      const newSellableItems =
        priorityParent === null
          ? reduced
          : { ...reduced, parent: priorityParent };
      sellableWhPrev.current = newSellableItems;
      return newSellableItems;
    } else {
      return sellableWhPrev.current;
    }
  }, [sellableInWh, whCode]);

  const effectiveByWh = company.hasNewLogistics
    ? { current: getLmsSellable(offer), parent: getLmsSellable(offer) }
    : sellableItemsByWh;

  // posibillity to order more than stock
  const isOriginalLogic =
    settingStore.settings.backorderLogic === BackorderLogic.ORIGINAL;

  if (!offer) {
    return {
      currentWhSellable: 0,
      parentWhSellable: 0,
      limit: 0,
    };
  }

  const isAvailableExpress = mounted && orderStore.isExpressAvailableNow;
  const parentExist = isArray(sellableInWh) && sellableInWh.length > 1;

  const getLimitStockOnly = (): number => {
    if (!offer) {
      return 0;
    }

    if (isAvailableExpress) {
      return effectiveByWh.current > effectiveByWh.parent
        ? effectiveByWh.current
        : effectiveByWh.parent;
    }

    if (parentExist && offer.isBackorderAvailable) {
      return effectiveByWh.parent;
    }

    return effectiveByWh.current;
  };

  const getLimitOriginal = (): number => {
    if (!offer) {
      return 0;
    }

    if (offer.isBackorderAvailable) {
      return OFFERS_LIMIT;
    }

    if (offer.multibuyPriceLevels?.length) {
      return Math.min(OFFERS_LIMIT, effectiveByWh.current || 0);
    }

    return effectiveByWh.current > OFFERS_LIMIT
      ? OFFERS_LIMIT
      : effectiveByWh.current;
  };

  const rawLimit = isOriginalLogic
    ? getLimitOriginal()
    : getLimitStockOnly() || offer.sellable;

  const limit =
    company.maxQuantityPerSku !== undefined
      ? Math.min(rawLimit, company.maxQuantityPerSku)
      : rawLimit;

  const currentWhSellable = effectiveByWh.current ?? 0;

  const parrentSellable = parentExist
    ? (effectiveByWh.parent ?? 0)
    : (effectiveByWh.current ?? 0);

  const parentWhSellable = isOriginalLogic
    ? getLimitOriginal()
    : parrentSellable;

  const laterPrecount = isOriginalLogic
    ? MAX_COUNT_TO_SHOW >= effectiveByWh.current
      ? MAX_COUNT_TO_SHOW + 1
      : effectiveByWh.current
    : parrentSellable;

  const laterCount =
    MAX_COUNT_TO_SHOW >= laterPrecount
      ? laterPrecount
      : `${MAX_COUNT_TO_SHOW}+`;

  return {
    currentWhSellable,
    parentWhSellable,
    limit,
    laterCount,
  };
};

export const getProductLimit = (offer?: StockSource | null, mounted = true) => {
  // posibillity to order more than stock
  const isOriginalLogic =
    settingStore.settings.backorderLogic === BackorderLogic.ORIGINAL;

  if (!offer) {
    return {
      currentWhSellable: 0,
      parentWhSellable: 0,
      limit: 0,
    };
  }

  const isAvailableExpress = mounted && orderStore.isExpressAvailableNow;
  // Without a selected address etaCalculation may hold an arbitrary default
  // zone, so only trust its warehouse when the user picked an address
  const whCode =
    (mounted &&
      userStore.deliveryAddress &&
      orderStore.etaCalculation?.warehouse?.code) ||
    company.config.warehouse.default;
  const sellableInWh =
    offer && offer?.sellableWarehouses && Object.keys(offer.sellableWarehouses);

  const parentExist = isArray(sellableInWh) && sellableInWh.length > 1;

  const reduced = isArray(sellableInWh)
    ? sellableInWh.reduce(
        (acc, key) => ({
          ...acc,
          [key === whCode ? 'current' : 'parent']:
            (Number(offer?.sellableWarehouses[key]) || 0) <= 0
              ? 0
              : offer.sellableWarehouses[key],
        }),
        { current: 0, parent: 0 },
      )
    : { current: sellableInWh, parent: 0 };

  const priorityParent = getPriorityParentStock(offer, whCode);
  const sellableItemsByWh =
    priorityParent === null ? reduced : { ...reduced, parent: priorityParent };

  const effectiveByWh = company.hasNewLogistics
    ? { current: getLmsSellable(offer), parent: getLmsSellable(offer) }
    : sellableItemsByWh;

  const getLimitStockOnly = (): number => {
    if (!offer) {
      return 0;
    }

    if (isAvailableExpress) {
      return effectiveByWh.current > effectiveByWh.parent
        ? effectiveByWh.current
        : effectiveByWh.parent;
    }

    if (parentExist && offer.isBackorderAvailable) {
      return effectiveByWh.parent;
    }

    return effectiveByWh.current;
  };

  const getLimitOriginal = (): number => {
    if (!offer) {
      return 0;
    }

    if (offer.isBackorderAvailable) {
      return OFFERS_LIMIT;
    }

    if (offer.multibuyPriceLevels?.length) {
      return Math.min(OFFERS_LIMIT, effectiveByWh.current || 0);
    }

    return effectiveByWh.current > OFFERS_LIMIT
      ? OFFERS_LIMIT
      : effectiveByWh.current;
  };

  const rawLimit = isOriginalLogic
    ? getLimitOriginal()
    : getLimitStockOnly() || offer.sellable;

  const limit =
    company.maxQuantityPerSku !== undefined
      ? Math.min(rawLimit, company.maxQuantityPerSku)
      : rawLimit;

  const currentWhSellable = effectiveByWh.current ?? 0;

  const parrentSellable = parentExist
    ? (effectiveByWh.parent ?? 0)
    : (effectiveByWh.current ?? 0);

  const parentWhSellable = isOriginalLogic
    ? getLimitOriginal()
    : parrentSellable;

  const laterPrecount = isOriginalLogic
    ? MAX_COUNT_TO_SHOW >= effectiveByWh.current
      ? MAX_COUNT_TO_SHOW + 1
      : effectiveByWh.current
    : parrentSellable;

  const laterCount =
    MAX_COUNT_TO_SHOW >= laterPrecount
      ? laterPrecount
      : `${MAX_COUNT_TO_SHOW}+`;

  return {
    currentWhSellable,
    parentWhSellable,
    limit,
    laterCount,
  };
};
